import React, { useState, useEffect } from 'react';
import { CheckCircle2, User, Hash, MapPin, Settings, ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { useVerifyOtp, useGenerateOtp } from '../../../../../auth/hooks';
import { useToast } from '@shared/hooks/use-toast';
import { Button } from '@shared/ui/button';
import { useTranslation } from 'react-i18next';

interface Props {
  onNext?: () => void;
  onPrevious?: () => void;
  allStepData?: any;
}

export const ConfirmOTP: React.FC<Props> = ({ onNext, onPrevious, allStepData }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const accountData = allStepData?.[0]?.accountData;
  const [otp, setOtp] = useState('');
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);


  const verifyOtpMutation = useVerifyOtp();
  const generateOtpMutation = useGenerateOtp();
  
  const handleResend = () => {
    if (!accountData?.email || !accountData?.remoteUtilityId) {
      toast({ title: 'Error', description: 'Missing account details to resend OTP', variant: 'destructive' });
      return;
    }
    
    generateOtpMutation.mutate(
      { 
        email: accountData.email, 
        remote_utility_id: accountData.remoteUtilityId 
      },
      {
        onSuccess: () => {
          toast({ title: 'Success', description: 'Verification code resent successfully' });
          setTimer(60);
          setOtp('');
        },
        onError: () => {
          toast({ title: 'Error', description: 'Failed to resend verification code', variant: 'destructive' });
        }
      }
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length === 6) {
      const secret_key = sessionStorage.getItem('guest_portal_secret_key') || '';
      const payload = {
        otp,
        secret_key
      };
      
      verifyOtpMutation.mutate(payload, {
        onSuccess: (res) => {
          console.log("OTP Verified", res);
          onNext?.();
        },
        onError: (err: any) => {
          console.error("OTP Verification Failed", err);
          const errorMessage = err?.response?.data?.message || err?.message || "Invalid OTP.";
          toast({
            title: "Verification Failed",
            description: errorMessage,
            variant: "destructive"
          });
        }
      });
    }
  };

  const maskName = (name: string) => {
    if (!name) return '';
    return name.split(' ').map(part => {
      if (part.length <= 1) return part;
      return part[0] + '*'.repeat(part.length - 1);
    }).join(' ');
  };

  const email = accountData?.email || sessionStorage.getItem('guest_portal_email') || '';
  const maskedEmail = email ? `${email.substring(0, 2)}***@${email.split('@')[1]}` : 'your email';

  const phone = accountData?.contactNumber || '';
  const maskedPhone = phone.length >= 4 ? `(***) ***-${phone.slice(-4)}` : '';

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Column */}
      <div className="flex-1 space-y-6">
        
        {/* Account Found Preview */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6 bg-green-50 p-4 rounded-xl border border-green-100">
            <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" />
            <div>
              <h3 className="font-bold text-gray-900 text-sm">{t("Account Found")}</h3>
              <p className="text-xs text-gray-500">{t("Confirm this is your account")}</p>
            </div>
          </div>

          <div className="space-y-5 px-2">
            <div className="flex items-start gap-3">
              <User className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{t("Name")}</p>
                <p className="text-sm font-semibold text-gray-900">{maskName(`${accountData?.firstName || ''} ${accountData?.lastName || ''}`.trim())}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Hash className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{t("Consumer No.")}</p>
                <p className="text-sm font-semibold text-gray-900">{accountData?.consumerNo}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{t("Address")}</p>
                <p className="text-sm font-semibold text-gray-900">{accountData?.addressData?.service?.ADDRESS || accountData?.addressData?.service?.address}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Settings className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{t("Services")}</p>
                <p className="text-sm font-semibold text-gray-900">{accountData?.utilityService?.length ? accountData.utilityService.join(', ') : t("NA")}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-blue-50/50 rounded-xl p-3 border border-blue-100 text-sm text-blue-600 flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border border-blue-600 flex items-center justify-center shrink-0"></div>
            {t("A 6-digit OTP has been sent to your registered phone and email.")}
          </div>
        </div>

        {/* OTP Input Form */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-1">{t("Enter OTP")}</h2>
          <p className="text-sm text-gray-500 mb-8">
            {t("Sent to")} {maskedPhone && <><span className="font-semibold text-gray-700">{maskedPhone}</span> · </>}<span className="font-semibold text-gray-700">{maskedEmail}</span>
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col items-center">
            <div className="mb-8 w-full max-w-[320px]">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="w-full h-16 border-2 border-gray-200 rounded-2xl bg-gray-50/50 text-center text-2xl tracking-[0.25em] font-bold text-gray-900 focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-600 outline-none transition-all placeholder:text-gray-400 placeholder:font-medium placeholder:tracking-normal placeholder:text-lg shadow-sm"
              />
            </div>

            {timer > 0 ? (
              <p className="text-sm text-gray-500 mb-6">
                {t("Resend in")} <span className="font-bold text-gray-900">{timer}s</span>
              </p>
            ) : (
              <button 
                type="button" 
                onClick={handleResend}
                disabled={generateOtpMutation.isPending}
                className="text-sm font-bold text-blue-600 mb-6 flex items-center gap-1.5 hover:text-blue-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${generateOtpMutation.isPending ? 'animate-spin' : ''}`} /> 
                {generateOtpMutation.isPending ? t("Resending...") : t("Resend verification code")}
              </button>
            )}


            <div className="w-full flex items-center gap-4">
              <Button 
                variant="outline"
                onClick={onPrevious}
                className=" flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> {t("Back")}
              </Button>
              
              <Button 
                variant="default"
                className='w-full'
                disabled={otp.length !== 6 || verifyOtpMutation.isPending}
              >
                {verifyOtpMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("Verify & Continue")} <ArrowRight className="w-4 h-4 inline-block ml-1" />
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Right Column */}
      <div className="w-full lg:w-[380px] space-y-4">
        <div className="bg-[#f8fafc] rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 text-green-600 font-bold mb-6">
            <CheckCircle2 className="w-5 h-5" /> {t("Account Found")}
          </div>

          <div className="space-y-4 text-sm mb-6 border-b border-gray-200 pb-6">
            <div className="flex justify-between">
              <span className="text-gray-500">{t("Name")}</span>
              <span className="font-semibold text-gray-900 text-right">{maskName(`${accountData?.firstName || ''} ${accountData?.lastName || ''}`.trim())}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t("Consumer No.")}</span>
              <span className="font-semibold text-gray-900 text-right">{accountData?.consumerNo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t("Account Type")}</span>
              <span className="font-semibold text-gray-900 text-right">{accountData?.ownershipDisplay}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t("Services")}</span>
              <span className="font-semibold text-gray-900 text-right">{accountData?.utilityService?.length ? accountData.utilityService.join(', ') : t("NA")}</span>
            </div>
          </div>

          <div className="flex justify-between items-end">
            <div>
              <p className="font-bold text-gray-900">{t("Unpaid Bills")}</p>
              <p className="text-xs text-gray-500">{t("{{count}} bills awaiting payment", { count: 2 })}</p>
            </div>
            <span className="text-lg font-bold text-red-600">${accountData?.unpaidBillsTotal?.toFixed(2)}</span>
          </div>
        </div>

        <div className="bg-[#f4f7f9] rounded-2xl p-5 border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <h4 className="font-bold text-gray-700 text-sm">Payment Security</h4>
          </div>
          <ul className="text-xs text-gray-500 space-y-2 font-medium">
            <li>• 256-bit TLS encryption</li>
            <li>• PCI DSS Level 1 compliant</li>
            <li>• Powered by Stripe</li>
            <li>• No card data stored on our servers</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const ArrowRight = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);
